/**
 * 代码格式化工具
 * 支持多种语言的代码格式化功能
 */

import { format as sqlFormat } from 'sql-formatter';
import { js as jsBeautify, css as cssBeautify, html as htmlBeautify } from 'js-beautify';

// 动态导入prettier以避免打包问题
let prettierFormat: any = null;
let parserBabel: any = null;
let parserTypeScript: any = null;

// 异步加载prettier
async function loadPrettier() {
  if (prettierFormat) return;

  try {
    // 使用 Promise.all 并行加载，并添加超时处理（可选）
    const [prettier, babel, typescript] = await Promise.all([
      import('prettier/standalone'),
      import('prettier/parser-babel'),
      import('prettier/parser-typescript'),
    ]);

    // 兼容不同的模块导出格式 (ESM / CommonJS)
    prettierFormat = prettier.format || (prettier as any).default?.format;
    parserBabel = babel.default || babel;
    parserTypeScript = typescript.default || typescript;

    if (!prettierFormat) {
      throw new Error('Prettier format function not found in loaded module');
    }
    console.log('Prettier loaded successfully');
  } catch (error) {
    console.warn('Failed to load prettier, falling back to other formatters:', error);
    // 确保即使加载失败也不会影响后续逻辑
    prettierFormat = null;
  }
}

export enum CodeLanguage {
  JAVASCRIPT = 'javascript',
  TYPESCRIPT = 'typescript',
  JSON = 'json',
  SQL = 'sql',
  HTML = 'html',
  CSS = 'css',
}

export interface FormatOptions {
  language: CodeLanguage;
  tabWidth?: number;
  useTabs?: boolean;
  printWidth?: number;
  semi?: boolean;
  singleQuote?: boolean;
  trailingComma?: 'none' | 'es5' | 'all';
}

/**
 * 检测并包装代码片段
 * @param code 原始代码
 * @param language 语言类型
 * @returns 包装后的代码和是否被包装的标志
 */
function wrapCodeFragment(
  code: string,
  language: CodeLanguage
): { wrappedCode: string; isWrapped: boolean } {
  if (language !== CodeLanguage.JAVASCRIPT && language !== CodeLanguage.TYPESCRIPT) {
    return { wrappedCode: code, isWrapped: false };
  }

  const trimmedCode = code.trim();

  // 检查是否是完整的代码（包含函数声明、类声明、变量声明等）
  const completePatterns = [
    /^(function|class|const|let|var|import|export)\s/,
    /^\{[\s\S]*\}$/, // 对象字面量
    /^\[[\s\S]*\]$/, // 数组字面量
    /^["'`][\s\S]*["'`]$/, // 字符串字面量
    /^\d+(\.\d+)?$/, // 数字字面量
    /^(true|false|null|undefined)$/, // 布尔值和特殊值
  ];

  // 如果匹配完整模式，不需要包装
  if (completePatterns.some((pattern) => pattern.test(trimmedCode))) {
    return { wrappedCode: code, isWrapped: false };
  }

  // 检查是否是函数体片段（以return、if、for等开头）
  const fragmentPatterns = [
    /^return\s/,
    /^if\s*\(/,
    /^for\s*\(/,
    /^while\s*\(/,
    /^switch\s*\(/,
    /^try\s*\{/,
    /^\w+\s*\(/, // 函数调用
    /^\w+\s*\./, // 属性访问
  ];

  if (fragmentPatterns.some((pattern) => pattern.test(trimmedCode))) {
    // 检查代码是否看起来不完整（缺少闭合括号等）
    const openBraces = (code.match(/\{/g) || []).length;
    const closeBraces = (code.match(/\}/g) || []).length;
    const openParens = (code.match(/\(/g) || []).length;
    const closeParens = (code.match(/\)/g) || []).length;
    const openBrackets = (code.match(/\[/g) || []).length;
    const closeBrackets = (code.match(/\]/g) || []).length;

    // 如果括号不匹配，尝试补全
    let wrappedCode = code;
    if (openBraces > closeBraces) {
      wrappedCode += '\n' + '}'.repeat(openBraces - closeBraces);
    }
    if (openParens > closeParens) {
      wrappedCode += ')'.repeat(openParens - closeParens);
    }
    if (openBrackets > closeBrackets) {
      wrappedCode += ']'.repeat(openBrackets - closeBrackets);
    }

    // 包装为函数体
    return {
      wrappedCode: `function temp() {\n${wrappedCode}\n}`,
      isWrapped: true,
    };
  }

  return { wrappedCode: code, isWrapped: false };
}

/**
 * 从包装的代码中提取原始内容
 * @param formattedCode 格式化后的代码
 * @param isWrapped 是否被包装过
 * @returns 提取后的代码
 */
function unwrapFormattedCode(formattedCode: string, isWrapped: boolean): string {
  if (!isWrapped) {
    return formattedCode;
  }

  // 提取函数体内容
  const match = formattedCode.match(/function temp\(\)\s*\{([\s\S]*)\}/);
  if (match && match[1]) {
    let content = match[1];

    // 移除首尾的空行
    content = content.replace(/^\s*\n/, '').replace(/\n\s*$/, '');

    // 如果内容有缩进，移除统一的缩进
    const lines = content.split('\n');
    if (lines.length > 0) {
      // 找到最小的非空行缩进
      const nonEmptyLines = lines.filter((line) => line.trim().length > 0);
      if (nonEmptyLines.length > 0) {
        const minIndent = Math.min(
          ...nonEmptyLines.map((line) => {
            const match = line.match(/^(\s*)/);
            return match ? match[1].length : 0;
          })
        );

        // 移除统一的缩进
        if (minIndent > 0) {
          const dedentedLines = lines.map((line) => {
            if (line.trim().length === 0) return line;
            return line.substring(minIndent);
          });
          content = dedentedLines.join('\n');
        }
      }
    }

    return content;
  }

  return formattedCode;
}

/**
 * 格式化代码
 * @param code 要格式化的代码
 * @param options 格式化选项
 * @returns 格式化后的代码
 */
export async function formatCode(code: string, options: FormatOptions): Promise<string> {
  const {
    language,
    tabWidth = 2,
    useTabs = false,
    printWidth = 80,
    semi = true,
    singleQuote = true,
    trailingComma = 'es5',
  } = options;

  try {
    switch (language) {
      case CodeLanguage.JAVASCRIPT:
        await loadPrettier();
        if (prettierFormat && parserBabel) {
          const { wrappedCode, isWrapped } = wrapCodeFragment(code, language);
          try {
            const formatted = prettierFormat(wrappedCode, {
              parser: 'babel',
              plugins: [parserBabel],
              tabWidth,
              useTabs,
              printWidth,
              semi,
              singleQuote,
              trailingComma,
            });
            return unwrapFormattedCode(formatted, isWrapped);
          } catch (prettierError) {
            // 如果prettier失败，回退到js-beautify
            console.warn('Prettier格式化失败，回退到js-beautify:', prettierError);
            return jsBeautify(code, {
              indent_size: tabWidth,
              indent_with_tabs: useTabs,
              max_preserve_newlines: 2,
              preserve_newlines: true,
              wrap_line_length: printWidth,
            });
          }
        } else {
          // 回退到js-beautify
          return jsBeautify(code, {
            indent_size: tabWidth,
            indent_with_tabs: useTabs,
            max_preserve_newlines: 2,
            preserve_newlines: true,
            wrap_line_length: printWidth,
          });
        }

      case CodeLanguage.TYPESCRIPT:
        await loadPrettier();
        if (prettierFormat && parserTypeScript) {
          const { wrappedCode, isWrapped } = wrapCodeFragment(code, language);
          try {
            const formatted = prettierFormat(wrappedCode, {
              parser: 'typescript',
              plugins: [parserTypeScript],
              tabWidth,
              useTabs,
              printWidth,
              semi,
              singleQuote,
              trailingComma,
            });
            return unwrapFormattedCode(formatted, isWrapped);
          } catch (prettierError) {
            // 如果prettier失败，回退到js-beautify
            console.warn('Prettier格式化失败，回退到js-beautify:', prettierError);
            return jsBeautify(code, {
              indent_size: tabWidth,
              indent_with_tabs: useTabs,
              max_preserve_newlines: 2,
              preserve_newlines: true,
              wrap_line_length: printWidth,
            });
          }
        } else {
          // 回退到js-beautify
          return jsBeautify(code, {
            indent_size: tabWidth,
            indent_with_tabs: useTabs,
            max_preserve_newlines: 2,
            preserve_newlines: true,
            wrap_line_length: printWidth,
          });
        }

      case CodeLanguage.JSON:
        try {
          const parsed = JSON.parse(code);
          return JSON.stringify(parsed, null, useTabs ? '\t' : ' '.repeat(tabWidth));
        } catch (error) {
          // 如果 JSON 解析失败，尝试使用 prettier
          await loadPrettier();
          if (prettierFormat && parserBabel) {
            try {
              return prettierFormat(code, {
                parser: 'json',
                plugins: [parserBabel],
                tabWidth,
                useTabs,
                printWidth,
              });
            } catch (prettierError) {
              // 如果prettier失败，回退到js-beautify
              console.warn('Prettier格式化失败，回退到js-beautify:', prettierError);
              return jsBeautify(code, {
                indent_size: tabWidth,
                indent_with_tabs: useTabs,
                max_preserve_newlines: 2,
                preserve_newlines: true,
                wrap_line_length: printWidth,
              });
            }
          } else {
            // 如果prettier不可用，回退到js-beautify
            return jsBeautify(code, {
              indent_size: tabWidth,
              indent_with_tabs: useTabs,
              max_preserve_newlines: 2,
              preserve_newlines: true,
              wrap_line_length: printWidth,
            });
          }
        }

      case CodeLanguage.SQL:
        return sqlFormat(code, {
          language: 'sql',
          tabWidth,
          useTabs,
          keywordCase: 'upper',
          functionCase: 'upper',
          dataTypeCase: 'upper',
        });

      case CodeLanguage.HTML:
        return htmlBeautify(code, {
          indent_size: tabWidth,
          indent_with_tabs: useTabs,
          max_preserve_newlines: 2,
          preserve_newlines: true,
          wrap_line_length: printWidth,
        });

      case CodeLanguage.CSS:
        return cssBeautify(code, {
          indent_size: tabWidth,
          indent_with_tabs: useTabs,
          max_preserve_newlines: 2,
          preserve_newlines: true,
          wrap_line_length: printWidth,
        });

      default:
        throw new Error(`不支持的语言类型: ${language}`);
    }
  } catch (error) {
    console.error('代码格式化失败:', error);
    throw new Error(`格式化失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

/**
 * 根据 CodeMirror 模式推断语言类型
 * @param mode CodeMirror 模式
 * @returns 语言类型
 */
export function inferLanguageFromMode(
  mode: string | { name: string; json?: boolean }
): CodeLanguage {
  if (typeof mode === 'string') {
    switch (mode.toLowerCase()) {
      case 'javascript':
      case 'js':
        return CodeLanguage.JAVASCRIPT;
      case 'typescript':
      case 'ts':
        return CodeLanguage.TYPESCRIPT;
      case 'json':
        return CodeLanguage.JSON;
      case 'sql':
        return CodeLanguage.SQL;
      case 'html':
        return CodeLanguage.HTML;
      case 'css':
        return CodeLanguage.CSS;
      default:
        return CodeLanguage.JAVASCRIPT; // 默认为 JavaScript
    }
  } else {
    const modeName = mode.name.toLowerCase();
    if (modeName === 'javascript') {
      return mode.json ? CodeLanguage.JSON : CodeLanguage.JAVASCRIPT;
    }
    return inferLanguageFromMode(modeName);
  }
}

/**
 * 快速格式化函数，使用默认配置
 * @param code 要格式化的代码
 * @param language 语言类型
 * @returns 格式化后的代码
 */
export async function quickFormat(code: string, language: CodeLanguage): Promise<string> {
  return await formatCode(code, { language });
}

/**
 * 根据 CodeMirror 模式快速格式化
 * @param code 要格式化的代码
 * @param mode CodeMirror 模式
 * @returns 格式化后的代码
 */
export async function formatByMode(
  code: string,
  mode: string | { name: string; json?: boolean }
): Promise<string> {
  const language = inferLanguageFromMode(mode);
  return await quickFormat(code, language);
}
